import { Loading } from '@shopify/polaris'
import { useEffect, useMemo, useState } from 'react'
import useSWR from 'swr'

import { backendFetchResultData } from '../common/api'
import { formatLocalISODate, queryString, useQuery } from '../common/helpers'
import { LoadErrorPage } from '../common/LoadErrorPage'
import { TabIndex, Tabs } from '../common/Tabs'
import { PageContent } from './PageContent'
import { Skeleton } from './Skeleton'

export interface FulfillmentEvent {
  subscriptionRestId?: number
  customerName?: string
  status: string
  date: string
  amount?: string
  orderGid?: string
  orderName?: string
}

const fetchDashboardFulfillments = (
  _: string,
  date: string,
  status: string,
  page: number
): Promise<FulfillmentEvent[]> =>
  backendFetchResultData(
    'GET',
    `/dashboard/fulfillments?${queryString({
      date,
      status,
      page,
    })}`
  )

export type FulfillmentOrderStatus = 'Scheduled' | 'Open' | 'Closed'

export interface PageQuery {
  date: string
  status: FulfillmentOrderStatus
  page: number
  event_types: string
}

export const usePageQuery = (): PageQuery => {
  const query = useQuery()

  const date = query.get('date') ?? formatLocalISODate(new Date())
  const status = (query.get('status') ?? 'Open') as FulfillmentOrderStatus
  const event_types = query.get('event_types') ?? ''
  const page = Number.parseInt(query.get('page') ?? '1') || 1

  return useMemo(
    () => ({
      date,
      status,
      page,
      event_types,
    }),
    [date, status, page, event_types]
  )
}

export const DashboardFulfillmentOrdersPage = () => {
  const pageQuery = usePageQuery()

  const { data, error, isValidating } = useSWR(
    ['dashboard-fulfillments', pageQuery.date, pageQuery.status],
    fetchDashboardFulfillments
  )

  const [fulfillments, setFulfillments] = useState<FulfillmentEvent[]>([])

  useEffect(() => {
    if (data) {
      setFulfillments(data)
    }
  }, [data])

  return (
    <>
      {isValidating && <Loading />}
      <Tabs selected={TabIndex.Dashboard} />

      {error && <LoadErrorPage />}
      {!error && !fulfillments && <Skeleton />}
      {!error && fulfillments && <PageContent fulfillments={fulfillments} />}
    </>
  )
}
